import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageIcon, Info, KeyRound, Loader2, Percent, Upload, UserCog } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TABS = [
  ["profil", "Profil", "tab-profil"],
  ["finans", "Finans/Vergi", "tab-finans"],
  ["marka", "Marka", "tab-marka"],
  ["sistem", "Sistem", "tab-sistem"],
];

function ProfileTab() {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/profile", {
        name: name !== user.name ? name : undefined,
        email: email !== user.email ? email : undefined,
      });
      setUser(data);
      if (data.email_pending) {
        toast.success(t("Doğrulama bağlantısı yeni e-posta adresinize gönderildi. Adres doğrulanana kadar mevcut e-postanızla giriş yapmaya devam edebilirsiniz."));
      } else {
        toast.success("Profil güncellendi");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== newPassword2) {
      toast.error("Yeni şifreler eşleşmiyor");
      return;
    }
    setSavingPassword(true);
    try {
      const { data } = await api.patch("/profile", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setUser(data);
      setCurrentPassword("");
      setNewPassword("");
      setNewPassword2("");
      toast.success("Şifreniz güncellendi");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <>
      <form onSubmit={saveProfile} className="border border-slate-200 bg-white rounded-md p-8 space-y-5"
        data-testid="profile-form">
        <div className="flex items-center gap-3">
          <UserCog className="h-5 w-5 text-blue-600" />
          <h2 className="font-display text-lg font-semibold text-[#0A1128]">{t("Hesap Bilgileri")}</h2>
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-name">{t("Ad Soyad")}</Label>
          <Input id="profile-name" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="profile-name-input" className="focus-visible:ring-blue-600" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="profile-email">{t("E-posta")}</Label>
          <Input id="profile-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="profile-email-input" className="focus-visible:ring-blue-600" />
          {user?.pending_email && (
            <p className="text-xs font-medium text-amber-600" data-testid="pending-email-note">
              {t("Doğrulama bekleniyor:")} {user.pending_email}
            </p>
          )}
        </div>
        <Button type="submit" disabled={savingProfile} data-testid="profile-save-button"
          className="bg-blue-600 hover:bg-blue-700 text-white">
          {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Kaydet")}
        </Button>
      </form>

      <form onSubmit={savePassword} className="mt-6 border border-slate-200 bg-white rounded-md p-8 space-y-5"
        data-testid="password-form">
        <div className="flex items-center gap-3">
          <KeyRound className="h-5 w-5 text-blue-600" />
          <h2 className="font-display text-lg font-semibold text-[#0A1128]">{t("Şifre Değiştir")}</h2>
        </div>
        <div className="space-y-2">
          <Label htmlFor="current-password">{t("Mevcut Şifre")}</Label>
          <Input id="current-password" type="password" required value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            data-testid="current-password-input" className="focus-visible:ring-blue-600" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">{t("Yeni Şifre")}</Label>
            <Input id="new-password" type="password" required minLength={8} value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input" className="focus-visible:ring-blue-600" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-password2">{t("Yeni Şifre (Tekrar)")}</Label>
            <Input id="new-password2" type="password" required minLength={8} value={newPassword2}
              onChange={(e) => setNewPassword2(e.target.value)}
              data-testid="new-password2-input" className="focus-visible:ring-blue-600" />
          </div>
        </div>
        <Button type="submit" disabled={savingPassword} data-testid="password-save-button"
          className="bg-blue-600 hover:bg-blue-700 text-white">
          {savingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Şifreyi Güncelle")}
        </Button>
      </form>
    </>
  );
}

function FinansTab() {
  const { t } = useLang();
  const { user } = useAuth();
  const canWrite = user?.role === "admin";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/org/settings")
      .then(({ data }) => setForm(data))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/org/settings", {
        default_currency: form.default_currency,
        default_kdv: Number(form.default_kdv),
        default_vade_days: Number(form.default_vade_days),
        mali_yil_start: form.mali_yil_start,
      });
      toast.success(t("Ayarlar kaydedildi"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  }

  return (
    <form onSubmit={save} className="border border-slate-200 bg-white rounded-md p-8 space-y-5" data-testid="org-settings-form">
      <div className="flex items-center gap-3">
        <Percent className="h-5 w-5 text-blue-600" />
        <h2 className="font-display text-lg font-semibold text-[#0A1128]">{t("Firma Finans Ayarları")}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t("Varsayılan Para Birimi")}</Label>
          <Select value={form.default_currency} disabled={!canWrite}
            onValueChange={(v) => setForm((f) => ({ ...f, default_currency: v }))}>
            <SelectTrigger data-testid="setting-currency" className="focus:ring-blue-600">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["TL", "USD", "EUR", "GBP"].map((c) => (
                <SelectItem key={c} value={c} data-testid={`setting-currency-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="setting-kdv">{t("Varsayılan KDV (%)")}</Label>
          <Input id="setting-kdv" type="number" min="0" max="100" step="0.01" value={form.default_kdv}
            disabled={!canWrite} onChange={(e) => setForm((f) => ({ ...f, default_kdv: e.target.value }))}
            data-testid="setting-kdv" className="focus-visible:ring-blue-600" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="setting-vade">{t("Varsayılan Vade (gün)")}</Label>
          <Input id="setting-vade" type="number" min="0" max="3650" value={form.default_vade_days}
            disabled={!canWrite} onChange={(e) => setForm((f) => ({ ...f, default_vade_days: e.target.value }))}
            data-testid="setting-vade" className="focus-visible:ring-blue-600" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="setting-mali-yil">{t("Mali Yıl Başlangıcı")}</Label>
          <Input id="setting-mali-yil" value={form.mali_yil_start} placeholder="01-01" disabled={!canWrite}
            onChange={(e) => setForm((f) => ({ ...f, mali_yil_start: e.target.value }))}
            data-testid="setting-mali-yil" className="focus-visible:ring-blue-600" />
          <p className="text-xs text-slate-400">AA-GG</p>
        </div>
      </div>
      {canWrite && (
        <Button type="submit" disabled={saving} data-testid="org-settings-save"
          className="bg-blue-600 hover:bg-blue-700 text-white">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Kaydet")}
        </Button>
      )}
    </form>
  );
}

function MarkaTab() {
  const { t } = useLang();
  const { user } = useAuth();
  const canWrite = user?.role === "admin";
  const [hasLogo, setHasLogo] = useState(false);
  const [logoVer, setLogoVer] = useState(0);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/org/settings").then(({ data }) => setHasLogo(Boolean(data.has_logo))).catch(() => {});
  }, []);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/org/logo", fd);
      toast.success(t("Logo yüklendi"));
      setHasLogo(true);
      setLogoVer(Date.now());
      setFile(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/org/logo?v=${logoVer}`;

  return (
    <div className="border border-slate-200 bg-white rounded-md p-8 space-y-5" data-testid="brand-tab">
      <div className="flex items-center gap-3">
        <ImageIcon className="h-5 w-5 text-blue-600" />
        <h2 className="font-display text-lg font-semibold text-[#0A1128]">{t("Firma Logosu")}</h2>
      </div>
      {hasLogo ? (
        <div className="border border-slate-200 rounded-md bg-slate-50 p-6 inline-block" data-testid="logo-preview">
          <img src={logoUrl} alt={t("Firma Logosu")} className="max-h-20 max-w-56 object-contain" />
        </div>
      ) : (
        <p className="text-sm text-slate-400" data-testid="logo-empty">{t("Henüz logo yüklenmedi")}</p>
      )}
      <p className="text-xs text-slate-400">{t("Logo, dışa aktarılan PDF çıktılarında kullanılır.")}</p>
      {canWrite && (
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-2">
            <Label htmlFor="logo-file">{t("Logo Yükle")}</Label>
            <Input id="logo-file" type="file" accept=".png,.jpg,.jpeg,.webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="logo-file-input" className="focus-visible:ring-blue-600" />
            <p className="text-xs text-slate-400">{t("PNG, JPG veya WebP — en fazla 2 MB")}</p>
          </div>
          <Button onClick={upload} disabled={!file || uploading} data-testid="logo-upload-button"
            className="bg-blue-600 hover:bg-blue-700 text-white">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" />{t("Yükle")}</>}
          </Button>
        </div>
      )}
    </div>
  );
}

function SistemTab() {
  const { t } = useLang();
  return (
    <div className="border border-slate-200 bg-white rounded-md p-8 space-y-4" data-testid="system-tab">
      <div className="flex items-center gap-3">
        <Info className="h-5 w-5 text-blue-600" />
        <h2 className="font-display text-lg font-semibold text-[#0A1128]">{t("Sistem")}</h2>
      </div>
      <div className="text-sm space-y-2.5">
        <div className="flex justify-between max-w-sm">
          <span className="text-slate-500">{t("Uygulama")}</span>
          <span className="font-medium text-[#0A1128]">TEMHA</span>
        </div>
        <div className="flex justify-between max-w-sm">
          <span className="text-slate-500">{t("Sürüm")}</span>
          <span className="font-medium text-[#0A1128] tabular">1.0</span>
        </div>
        <div className="flex justify-between max-w-sm">
          <span className="text-slate-500">{t("Bağlantı")}</span>
          <span className="font-medium text-green-700">{t("Bağlı")}</span>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { t } = useLang();
  const [tab, setTab] = useState("profil");

  return (
    <div className="fade-up max-w-2xl" data-testid="settings-page">
      <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A1128]">{t("Ayarlar")}</h1>
      <p className="mt-2 text-slate-500">{t("Hesap, firma ve marka ayarlarınızı yönetin.")}</p>

      <div className="mt-8 flex gap-1 border-b border-slate-200" data-testid="settings-tabs">
        {TABS.map(([key, label, tid]) => (
          <button key={key} type="button" onClick={() => setTab(key)} data-testid={tid}
            className={`px-4 py-2.5 text-sm font-medium transition-colors duration-200 border-b-2 -mb-px ${
              tab === key ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-[#0A1128]"
            }`}>
            {t(label)}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "profil" && <ProfileTab />}
        {tab === "finans" && <FinansTab />}
        {tab === "marka" && <MarkaTab />}
        {tab === "sistem" && <SistemTab />}
      </div>
    </div>
  );
}
